import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Shift, Event, Question, AnswerType } from '@amzn/red-velvet-api';
import Button from '@amzn/awsui-components-react/polaris/button';
import Modal, { ModalProps } from '@amzn/awsui-components-react/polaris/modal';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Checkbox from '@amzn/awsui-components-react/polaris/checkbox';
import Form from '@amzn/awsui-components-react/polaris/form';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Multiselect from '@amzn/awsui-components-react/polaris/multiselect';
import { ComplexSignupInputs } from '../../hooks/betterEvents';
import { notNull } from '../../utils/tsUtils';
import { useUser } from '../../hooks/user';
import { useCreateSignup } from '../../hooks/shifts/shiftSignup';
import { useFeatures, featureIsEnabled } from '../../hooks/features';
import { EVENT_CREATION } from '../../utils/constants';
import { getSignupVisibleQuestions } from '../../utils/signupUtils';
import { formatCharacterCount } from '../../utils/eventUtils';

const TEXT_ANSWER_MAX_LENGTH = 100;

interface ComplexSignupDialogProps {
  event: Event;
  shift: Shift;
  visible: boolean;
  onDismiss: NonNullable<ModalProps['onDismiss']>;
}

export function useComplexEventState(event: Event, isEventCreationEnabled: boolean) {
  const [waiverLinkOpened, setWaiverLinkOpened] = useState(false);
  const defaultAnswers = () => {
    return event.questions
      ? event.questions
          .map((question: Question) => {
            if (isEventCreationEnabled && question.answerType === AnswerType.TEXT_SINGLE) {
              return { questionId: question.id, textValue: '' };
            }
            if (isEventCreationEnabled && question.answerType === AnswerType.CHOICES_MULTIPLE) {
              return null; // entries added dynamically by toggleMultiChoiceOption
            }
            if (question.options) {
              return { questionId: question.id, optionId: undefined };
            }
            return null;
          })
          .filter(notNull)
      : [];
  };
  const [complexSignupData, setComplexSignupData] = useState<ComplexSignupInputs>(() => ({
    guestCount: event.allowGuests ? 0 : undefined,
    tShirtSizeId: event.hasTShirt ? 1 : undefined,
    waiverAccepted: event.waiver ? false : undefined,
    questionAnswers: defaultAnswers(),
  }));

  return {
    complexSignupData,
    waiverLinkOpened,
    updaters: {
      reset: () => {
        setComplexSignupData({
          guestCount: event.allowGuests ? 0 : undefined,
          tShirtSizeId: event.hasTShirt ? 1 : undefined,
          waiverAccepted: event.waiver ? false : undefined,
          questionAnswers: defaultAnswers(),
        });
        setWaiverLinkOpened(false);
      },
      setWaiverLinkOpened,
      setQuestionAnswers: (questionId?: string, newOptionId?: string) => {
        setComplexSignupData((prev) => ({
          ...prev,
          questionAnswers: prev.questionAnswers?.map((qa) =>
            qa.questionId === questionId
              ? {
                  ...qa,
                  optionId: newOptionId,
                }
              : qa
          ),
        }));
      },
      setTextAnswer: (questionId: string, textValue: string) => {
        setComplexSignupData((prev) => ({
          ...prev,
          questionAnswers: prev.questionAnswers?.map((qa) =>
            qa.questionId === questionId ? { ...qa, textValue } : qa
          ),
        }));
      },
      toggleMultiChoiceOption: (questionId: string, optionId: string) => {
        setComplexSignupData((prev) => {
          const answers = prev.questionAnswers ?? [];
          const exists = answers.some(
            (qa) => qa.questionId === questionId && qa.optionId === optionId
          );
          return {
            ...prev,
            questionAnswers: exists
              ? answers.filter((qa) => !(qa.questionId === questionId && qa.optionId === optionId))
              : [...answers, { questionId, optionId }],
          };
        });
      },
      setTshirtSizeId: (newValue: number) => {
        setComplexSignupData((prev) => ({
          ...prev,
          tShirtSizeId: event.hasTShirt ? newValue : undefined,
        }));
      },
      setGuestCount: (newValue: number) => {
        setComplexSignupData((prev) => ({
          ...prev,
          guestCount: event.allowGuests ? newValue : undefined,
        }));
      },
      setWaiverAccepted: (newValue: boolean) => {
        setComplexSignupData((prev) => ({
          ...prev,
          waiverAccepted: event.waiver ? newValue : undefined,
        }));
      },
    },
  };
}

function isQuestionAnswered(
  question: Question,
  answers: ComplexSignupInputs['questionAnswers'],
  isEventCreationEnabled: boolean
): boolean {
  if (isEventCreationEnabled && question.answerType === AnswerType.TEXT_SINGLE) {
    return (
      answers?.some(
        (a) => a.questionId === question.id && a.textValue && a.textValue.trim().length > 0
      ) ?? false
    );
  }
  if (isEventCreationEnabled && question.answerType === AnswerType.CHOICES_MULTIPLE) {
    return answers?.some((a) => a.questionId === question.id && a.optionId !== undefined) ?? false;
  }
  // CHOICES_SINGLE or legacy Benevity questions (no answerType)
  return answers?.some((a) => a.questionId === question.id && a.optionId !== undefined) ?? false;
}

function validateData(
  event: Event,
  shift: Shift,
  waiverLinkOpened: boolean,
  complexSignupData: ComplexSignupInputs,
  isEventCreationEnabled: boolean
) {
  const isAllowGuestNullOrValid =
    !event.allowGuests ||
    (shift.maxSignup ? (complexSignupData.guestCount ?? 0) + 1 <= shift.signupsRemaining : true);
  const areQuestionsNullOrValid =
    !event.questions ||
    event.questions.every((q) =>
      isQuestionAnswered(q, complexSignupData.questionAnswers, isEventCreationEnabled)
    );
  const isWaiverNullOrValid =
    !(event.waiver && event.waiver.url) ||
    event.waiver.acceptance !== 'required' ||
    (complexSignupData.waiverAccepted && waiverLinkOpened);
  return isAllowGuestNullOrValid && areQuestionsNullOrValid && isWaiverNullOrValid;
}

function RequiredLabel({ label, isRequired }: { label: string; isRequired: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      {t(label)}
      {isRequired && <span style={{ color: 'red' }}> *</span>}
    </>
  );
}

export function ComplexSignupDialog(props: ComplexSignupDialogProps) {
  const { t } = useTranslation();
  const { event, shift, visible, onDismiss } = props;
  const signupMutation = useCreateSignup(event.eventId, shift.shiftId);
  const user = useUser();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const signupEvent = {
    ...event,
    questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
  };

  const tshirtSizeOptions: SelectProps.Options = t(
    'eventDetails.shiftDisplay.complexSignup.tshirtSizes',
    {
      returnObjects: true,
    }
  );

  const hasLimitedSpots = shift.signupsRemaining && shift.maxSignup;

  const { complexSignupData, waiverLinkOpened, updaters } = useComplexEventState(
    signupEvent,
    isEventCreationEnabled
  );

  return (
    <Modal
      onDismiss={(event) => {
        onDismiss(event);
        updaters.reset();
      }}
      visible={visible}
      header={event.title}
      closeAriaLabel={t('eventDetails.shiftDisplay.complexSignup.closeButton')}
    >
      <h3>
        <b>{shift.name}</b>
      </h3>
      <p>
        <b>{t('eventDetails.shiftDisplay.date')}:</b> {shift.startDateTime?.toDateString()}
      </p>
      <p>
        <b>{t('eventDetails.shiftDisplay.startTime')}:</b> {shift.startDateTime?.toTimeString()}
      </p>
      <p>
        <b>{t('eventDetails.shiftDisplay.spotsAvailable')}:</b>{' '}
        {hasLimitedSpots ? shift.signupsRemaining : t('eventDetails.shiftDisplay.unlimited')}
      </p>

      {/* Enables submitting data when pressing enter or using a screenreader */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form>
          <SpaceBetween size={'m'}>
            {event.allowGuests ? (
              <FormField
                key={`allowGuestsKey.${shift.shiftId}.addGuests`}
                label={
                  <RequiredLabel
                    label="eventDetails.shiftDisplay.complexSignup.addGuest"
                    isRequired={false}
                  />
                }
                warningText={
                  shift.maxSignup &&
                  1 + (complexSignupData.guestCount ?? 0) > shift.signupsRemaining
                    ? t('eventDetails.shiftDisplay.complexSignup.guestsOverLimit', {
                        allowGuests: shift.signupsRemaining - 1,
                      })
                    : null
                }
              >
                <Input
                  // Required for integration test to target css selector instead of text selector.
                  data-testid={'complexSignup.addGuests'}
                  onChange={({ detail }) => {
                    const selectNumGuests = Number.parseInt(detail.value);
                    if (selectNumGuests >= 0) {
                      // unlimited signups
                      if (!shift.maxSignup) {
                        updaters.setGuestCount(selectNumGuests);
                      } else {
                        if (selectNumGuests <= shift.signupsRemaining) {
                          updaters.setGuestCount(selectNumGuests);
                        }
                      }
                    }
                  }}
                  value={(complexSignupData.guestCount ?? 0).toString()}
                  inputMode="numeric"
                  type="number"
                />
              </FormField>
            ) : null}

            {event.hasTShirt ? (
              <FormField
                key={`tShirtKey.${shift.shiftId}.sizeSelection`}
                label={
                  <RequiredLabel
                    label="eventDetails.shiftDisplay.complexSignup.tshirt"
                    isRequired={false}
                  />
                }
              >
                <Select
                  // Adding data-testid to target clickleable button that gets rendered in tests.
                  data-testid={'complexSignup.sizeSelection'}
                  onChange={({ detail }) =>
                    updaters.setTshirtSizeId(
                      detail.selectedOption.value ? Number.parseInt(detail.selectedOption.value) : 1
                    )
                  }
                  selectedOption={
                    tshirtSizeOptions.find(
                      (option) => option.value === complexSignupData.tShirtSizeId?.toString()
                    ) ?? null
                  }
                  options={tshirtSizeOptions}
                  placeholder={t('eventDetails.shiftDisplay.complexSignup.chooseTshirt')}
                />
              </FormField>
            ) : null}

            {signupEvent.questions
              ? signupEvent.questions.map((question: Question) => {
                  if (isEventCreationEnabled && question.answerType === AnswerType.TEXT_SINGLE) {
                    const answer = complexSignupData.questionAnswers?.find(
                      (a) => a.questionId === question.id
                    );
                    return (
                      <FormField
                        key={question.id}
                        label={<RequiredLabel label={question.title ?? ''} isRequired={true} />}
                        constraintText={
                          <div style={{ textAlign: 'right' }}>
                            {formatCharacterCount(
                              answer?.textValue?.length ?? 0,
                              TEXT_ANSWER_MAX_LENGTH
                            )}
                          </div>
                        }
                      >
                        <Input
                          data-testid={`complexSignup.customQuestion-id-${question.id}`}
                          value={answer?.textValue ?? ''}
                          onChange={({ detail }) => {
                            if (detail.value.length <= TEXT_ANSWER_MAX_LENGTH && question.id) {
                              updaters.setTextAnswer(question.id, detail.value);
                            }
                          }}
                          placeholder={t(
                            'eventDetails.shiftDisplay.complexSignup.textAnswerPlaceholder'
                          )}
                        />
                      </FormField>
                    );
                  }
                  if (
                    isEventCreationEnabled &&
                    question.answerType === AnswerType.CHOICES_MULTIPLE
                  ) {
                    return (
                      <FormField
                        key={question.id}
                        label={<RequiredLabel label={question.title ?? ''} isRequired={true} />}
                      >
                        <Multiselect
                          data-testid={`complexSignup.customQuestion-id-${question.id}`}
                          selectedOptions={
                            question.options
                              ?.filter((option) =>
                                complexSignupData.questionAnswers?.some(
                                  (a) => a.questionId === question.id && a.optionId === option.id
                                )
                              )
                              .map((option) => ({ label: option.label, value: option.id })) ?? []
                          }
                          options={question.options?.map((option) => ({
                            label: option.label,
                            value: option.id,
                          }))}
                          onChange={({ detail }) => {
                            if (!question.id) return;
                            const qId = question.id;
                            const selectedIds = detail.selectedOptions.map((o) => o.value);
                            const currentIds = (complexSignupData.questionAnswers ?? [])
                              .filter((a) => a.questionId === qId && a.optionId)
                              .map((a) => a.optionId);
                            // Toggle added options
                            selectedIds.forEach((id) => {
                              if (id && !currentIds.includes(id)) {
                                updaters.toggleMultiChoiceOption(qId, id);
                              }
                            });
                            // Toggle removed options
                            currentIds.forEach((id) => {
                              if (id && !selectedIds.includes(id)) {
                                updaters.toggleMultiChoiceOption(qId, id);
                              }
                            });
                          }}
                          placeholder={t('eventDetails.shiftDisplay.complexSignup.chooseOptions')}
                        />
                      </FormField>
                    );
                  }
                  // CHOICES_SINGLE or legacy Benevity questions (no answerType)
                  if (!question.options) return null;
                  const selectedOpt = question.options.find((option) =>
                    complexSignupData.questionAnswers?.some(
                      (a) => a.questionId === question.id && a.optionId === option.id
                    )
                  );
                  return (
                    <FormField
                      key={question.id}
                      label={<RequiredLabel label={question.title ?? ''} isRequired={true} />}
                    >
                      <Select
                        data-testid={`complexSignup.customQuestion-id-${question.id}`}
                        onChange={({ detail }) => {
                          updaters.setQuestionAnswers(question.id, detail.selectedOption.value);
                        }}
                        selectedOption={
                          selectedOpt
                            ? { label: selectedOpt.label ?? '', value: selectedOpt.id }
                            : null
                        }
                        options={question.options.map((option) => ({
                          label: option.label,
                          value: option.id,
                        }))}
                        placeholder={t('eventDetails.shiftDisplay.complexSignup.chooseOptions')}
                      />
                    </FormField>
                  );
                })
              : null}

            {event.waiver && event.waiver.url && event.waiver.acceptance !== 'none' ? (
              <FormField
                key={`waiverKey.${shift.shiftId}.${event.waiver.acceptance}`}
                label={
                  <RequiredLabel
                    label="eventDetails.shiftDisplay.complexSignup.waiver"
                    isRequired={event.waiver.acceptance === 'required'}
                  />
                }
              >
                <SpaceBetween size="s">
                  <FormField key={`waiverKey.${shift.shiftId}.clickViewWaiver`}>
                    <a
                      target="_blank"
                      href={event.waiver.url}
                      onContextMenu={() => updaters.setWaiverLinkOpened(true)}
                      onClick={() => updaters.setWaiverLinkOpened(true)}
                      rel="noreferrer"
                    >
                      {t('eventDetails.shiftDisplay.complexSignup.clickViewWaiver')}
                    </a>
                    <Checkbox
                      checked={complexSignupData.waiverAccepted ?? false}
                      onChange={({ detail }) => updaters.setWaiverAccepted(detail.checked)}
                    >
                      <RequiredLabel
                        label="eventDetails.shiftDisplay.complexSignup.acceptWaiver"
                        isRequired={event.waiver.acceptance === 'required'}
                      />
                    </Checkbox>
                  </FormField>
                </SpaceBetween>
              </FormField>
            ) : null}
            <Button
              fullWidth
              loading={signupMutation.isPending}
              disabled={
                !validateData(
                  signupEvent,
                  shift,
                  waiverLinkOpened,
                  complexSignupData,
                  isEventCreationEnabled
                )
              }
              variant="primary"
              data-aci-analytics-name={`shifts_complex_signup`}
              onClick={() => {
                signupMutation.mutate(
                  {
                    eventId: shift.eventId,
                    shift,
                    request: { ...complexSignupData, shift: shift.shiftId, alias: user.userId },
                  },
                  {
                    onSettled: () => {
                      updaters.reset();
                      // Note: the 'closeButton' input here indicates that the modal was closed by an action within the modal
                      // since the dismiss function only accepts the following reasons: 'closeButton', 'overlay', 'keyboard'
                      // and overlay and keyboard indicate user events that are external to the modal
                      props.onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
                    },
                  }
                );
              }}
            >
              {t('eventDetails.shiftDisplay.register')}
            </Button>
          </SpaceBetween>
        </Form>
      </form>
    </Modal>
  );
}
